import { useCallback, useState } from "react";
import { Alert, Box, Button, Snackbar } from "@mui/material";
import { MEMBERS_IMPORT_ENDPOINT } from "../../config/urls";
import AddMemberModal from "./AddMemberModal";
import MemberDetail from "./MemberDetail";
import MembersListView from "./MembersListView";
import UploadMembersModal from "./UploadMembersModal";
import { messageFrom, useMembersApi } from "./membersApi";

/**
 * Members (BCAS): list, member detail, Add member and the 2-step file upload.
 * Shown by the sidebar only when the tenant has the members module (apps includes "members").
 */
const Members = () => {
  const api = useMembersApi();
  const [openMemberId, setOpenMemberId] = useState(null);
  const [modal, setModal] = useState(null); // null | "add" | "upload"
  const [toast, setToast] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  const notify = useCallback((message, { severity = "success", action = null } = {}) => {
    setToast({ message, severity, action, id: Date.now() });
  }, []);

  const handleAdded = (member) => {
    setModal(null);
    notify(`${`${member.salutation || ""} ${member.full_name || ""}`.trim() || member.mobile} added`);
    refresh();
  };

  const handleImported = (job) => {
    setModal(null);
    const action =
      job.skipped_count > 0 && job.has_error_report
        ? {
            label: "Download error report",
            onClick: () =>
              api
                .download(`${MEMBERS_IMPORT_ENDPOINT}${job.id}/errors.csv`, "member_import_errors.csv")
                .catch((error) => notify(messageFrom(error), { severity: "error" })),
          }
        : null;
    notify(`${job.created_count} created · ${job.updated_count} updated · ${job.skipped_count} skipped`, { action });
    refresh();
  };

  const openFromModal = (id) => {
    setModal(null);
    setOpenMemberId(id);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {openMemberId ? (
        <MemberDetail
          key={openMemberId}
          api={api}
          memberId={openMemberId}
          notify={notify}
          onBack={() => setOpenMemberId(null)}
        />
      ) : (
        <MembersListView
          api={api}
          refreshKey={refreshKey}
          notify={notify}
          onOpenMember={setOpenMemberId}
          onAdd={() => setModal("add")}
          onUpload={() => setModal("upload")}
        />
      )}

      {modal === "add" && (
        <AddMemberModal api={api} onClose={() => setModal(null)} onSaved={handleAdded} onOpenMember={openFromModal} />
      )}
      {modal === "upload" && <UploadMembersModal api={api} onClose={() => setModal(null)} onDone={handleImported} />}

      <Snackbar
        key={toast?.id}
        open={Boolean(toast)}
        autoHideDuration={toast?.action ? 12000 : 6000}
        onClose={(event, reason) => reason !== "clickaway" && setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            action={
              toast.action ? (
                <Button color="inherit" size="small" onClick={toast.action.onClick}>
                  {toast.action.label}
                </Button>
              ) : undefined
            }
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default Members;
